import { useState, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { TiptapEditor } from "@/components/admin/TiptapEditor"

interface PublishModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onPublish: (changeSummary: string, publishedAt: string, adminOnly: boolean) => Promise<void>
  isPublishing: boolean
}

export function PublishModal({
  open,
  onOpenChange,
  onPublish,
  isPublishing,
}: PublishModalProps) {
  const [changeSummary, setChangeSummary] = useState("")
  const [adminOnly, setAdminOnly] = useState(false)
  const [publishDate, setPublishDate] = useState(
    () => new Date().toISOString().split("T")[0]
  )

  useEffect(() => {
    if (open) {
      setChangeSummary("")
      setAdminOnly(false)
      setPublishDate(new Date().toISOString().split("T")[0])
    }
  }, [open])

  const handlePublish = async () => {
    await onPublish(changeSummary, publishDate, adminOnly)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Publish Version</DialogTitle>
          <DialogDescription>
            Publish this draft version to make it live. Provide a summary of
            changes for documentation purposes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Change Summary</Label>
            <TiptapEditor
              content={changeSummary}
              onChange={setChangeSummary}
              showCitationButton={false}
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="admin-only-change"
              checked={adminOnly}
              onCheckedChange={(checked) => setAdminOnly(checked === true)}
            />
            <Label htmlFor="admin-only-change" className="font-normal cursor-pointer">
              Admin-only change (not visible to customers)
            </Label>
          </div>
          <div className="space-y-2">
            <Label>Publish Date</Label>
            <Input
              type="date"
              value={publishDate}
              max={new Date().toISOString().split("T")[0]}
              onChange={(e) => setPublishDate(e.target.value)}
              className="w-48"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handlePublish}
            disabled={isPublishing}
          >
            {isPublishing ? "Publishing..." : "Publish"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
